import React, { useEffect, useState } from 'react';
import Placeholder from './Placholder';
import { Column, CommonModal, DateInput, LoadingBar, LoadingButtonV4, MultiSelect, PrimaryButton, Select } from '../Common';
import Routes from '../../routes';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Box, Grid } from '@mui/material';
import { AllOption, checkIsDefaultOption, fetchReportExport, getLocationFiltersDropdown, getUpdatedLocation, getValueOrDefault, renderWeight, validateDate, validateRange } from './utils';
import RangeInput from '../Common/RangeInput';
import UserInfo from '../../UserInfo';

const Greenchill = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [reportModal, setReportModal] = useState(false);
    const [maxChargeCapacity, setMaxChargeCapacity] = useState();
    const [ErrorObject, setErrorObject] = useState({});
    const [formData, setFormData] = useState({
        regions: [],
        locations: [],
        states: [],
        chargeCapacityMin: 0,
        chargeCapacityMax: "",
        startDate: "",
        endDate: "",
        assetTypes: []
    });
    
    const setDefaultFormData = (data) => {
        const maxChargeCapacity = renderWeight(data.maxCapacity);
        setFormData({
            states: [data.states[AllOption]],
            locations: [data.locations[AllOption]],
            regions: data.regions[AllOption],
            chargeCapacityMin: 0,
            chargeCapacityMax: maxChargeCapacity,
            startDate: `01/01/${moment().year()}`,
            endDate: `${moment().format('MM/DD/YYYY')}`,
            assetTypes: [data.assetTypes[0], data.assetTypes[1], data.assetTypes[2]],
        });

        setData({
            ...data, 
            filterLocations: data.locations,
            filterStates: data.states,
            filterRegions: data.regions,
        });
        setMaxChargeCapacity(maxChargeCapacity);
    };

    const fetchDropdown = async () => {
        try {
            const response = await fetch(Routes.PowerBI.EpaDropdown());
            const data = await response.json();
            setDefaultFormData(data);
            setMaxChargeCapacity(renderWeight(data.maxCapacity));
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDropdown();
    }, []);


    const validateFilter = (filter) => {
        const errorObject = {};
        const isEmpty = (value) =>
            value === null || value === undefined || value === "" || value.length === 0;

        filter.forEach(({ Property, Value }) => {
            if (isEmpty(Value)) {
                switch (Property) {
                    case "startDate":
                        errorObject.startDate = "Start Date";
                        break;
                    case "endDate":
                        errorObject.endDate = "End Date";
                        break;
                    default:
                        break;
                }
            } else {
                switch (Property) {
                    case "date":
                        if (!validateDate(filter)) {
                            errorObject.date = "Start Date-End Date";
                        }
                        break;
                    case "assetCapacity":
                        if (!validateRange(Value, maxChargeCapacity, 0, renderWeight(50))) {
                            errorObject.assetCapacity = "Asset Charge Capacity";
                        }
                        break;
                    default:
                        break;
                }
            }
        });

        const hasError = Object.keys(errorObject).length > 0;
        if (hasError) {
            const errorMessages = Object.keys(errorObject)
                .map((key) => errorObject[key])
                .join(" and ");
            toast.error(
                `All required filters must be applied to generate the report. Please select the filter(s) ${errorMessages} and try again.`,
                { duration: 4000 }
            );
        }
        setErrorObject(errorObject);
        return hasError;
    };

    const onHandleLocationRegionChange = (value, type) => {
        if(type === "regions"){
            value = [value]
        }
        const { regionFilter, stateFilter, locationFilter, newChargeCapacityMax } = getLocationFiltersDropdown(value, type, data, formData, maxChargeCapacity);
        setData(prev => ({
            ...prev,
            filterRegions: regionFilter,
            filterStates: stateFilter,
            filterLocations: locationFilter,
        }));

        const { locations } = getUpdatedLocation(value, type, formData, data.locations);
        const newChargeCapacityMin =  newChargeCapacityMax < formData.chargeCapacityMin ? 0 : formData.chargeCapacityMin;

        setFormData(prev => ({
            ...prev,
            locations: locations,
            chargeCapacityMin: newChargeCapacityMin,
            chargeCapacityMax: newChargeCapacityMax,
        }));
        setMaxChargeCapacity(newChargeCapacityMax);
    };

    const exportReport = async () => {

        const startDate = formData.startDate ? moment(formData.startDate).format('MM/DD/YYYY') : '';
        const endDate = formData.endDate ? moment(formData.endDate).format('MM/DD/YYYY') : '';
        const filter = [
            {
                Property: "locations",
                Value: formData.locations.map(location => location.value).toString()
            },
            {
                Property: "assetCapacity",
                Value: `${formData.chargeCapacityMin}-${formData.chargeCapacityMax}`
            },
            startDate && endDate && {
                Property: "date",
                Value: `${startDate}-${endDate}`
            },
            {
                Property: "regions",
                Value: formData.regions.value.toString()
            },
            {
                Property: "assetTypes",
                Value: formData.assetTypes.map(asset => asset.value).toString()
            },
            {
                Property: "states",
                Value: formData.states.map(st => st.value).toString()
            },
            {
                Property: "startDate",
                Value: startDate
            },
            {
                Property: "endDate",
                Value: endDate
            },
        ].filter(Boolean);
        if (validateFilter(filter)) {
            return;
        }
        setReportModal(false);
        setDefaultFormData(data);
        await fetchReportExport({
            url: Routes.PowerBI.ExportGreenchillReport(),
            body: {
                Id: 16,
                Filters: filter,
            }
        });
    };

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <Placeholder 
                message={'Customize and export your report by clicking the button below.'}
                subMessage={''}
            >
                <Grid item xs={12} md={3} sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        marginBottom: '2rem' 
                    }}>
                    <PrimaryButton
                        buttonText="GENERATE REPORT (.xlsx)"
                        onClick={() => { 
                            setReportModal(true);
                        }}
                    />
                </Grid>
            </Placeholder>

            <CommonModal
                open={reportModal}
                close={() => setReportModal(false)}
                header="Generate EPA GreenChill Report"
            >
                <Grid container spacing={3} align='center'>

                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <DateInput
                            label="Start Date"
                            isRequired
                            variant={"standard"}
                            allowPast={true}
                            date={formData.startDate}
                            onChange={date => {
                                setFormData(prev => ({
                                    ...prev,
                                    startDate: date,
                                }));
                            }}
                            error={ErrorObject.startDate}
                        />
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <DateInput
                            label="End Date"
                            isRequired
                            variant={"standard"}
                            allowPast={true}
                            date={formData.endDate}
                            onChange={date => {
                                setFormData(prev => ({
                                    ...prev,
                                    endDate: date,
                                }));
                            }}
                            error={ErrorObject.endDate}
                        />
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <Select
                            isFilterObject
                            label='Region/Division *'
                            variant={"standard"}
                            showNullOption={false}
                            options={data.filterRegions}
                            value={formData.regions}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.regions, "regions", formData);
                                setFormData(prev => ({
                                    ...prev,
                                    regions: newValue,
                                }));
                                onHandleLocationRegionChange(newValue, 'regions');
                            }}
                        >
                        </Select>
                    </Grid>

                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <MultiSelect 
                            label='Location Name *'
                            isFilterObject
                            variant={"standard"}
                            options={data.filterLocations}
                            value={formData.locations}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.locations, "locations", formData);
                                setFormData(prev => ({
                                    ...prev,
                                    locations: newValue
                                }));
                                onHandleLocationRegionChange(newValue, 'locations');
                            }}
                        >
                        </MultiSelect>
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <MultiSelect 
                            label='Location State *'
                            isFilterObject
                            variant={"standard"}
                            options={data.filterStates}
                            value={formData.states}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.states, "states", formData);
                                setFormData(prev => ({
                                    ...prev,
                                    states: newValue
                                }));
                            }}
                        >
                        </MultiSelect>
                    </Grid>

                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <RangeInput
                            label={`Asset Capacity (${UserInfo.weightUnits})`}
                            startRange={formData.chargeCapacityMin}
                            endRange={formData.chargeCapacityMax}
                            disableEndRangeInput
                            onClose={(start, end) => setFormData(prev => ({
                                ...prev,
                                chargeCapacityMin: start,
                                chargeCapacityMax: end,
                            }))}
                        />
                    </Grid>

                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <MultiSelect 
                            label='Asset Type'
                            isFilterObject
                            variant={"standard"}
                            disabledMenuItems={[data.assetTypes[0]]}
                            renderSeparator={" and "}
                            options={data.assetTypes}
                            value={formData.assetTypes}
                            onChange={e => {
                                const {
                                    target: { value },
                                } = e;
                                setFormData(prev => ({
                                    ...prev,
                                    assetTypes: value
                                }));
                            }}
                        >
                        </MultiSelect>
                    </Grid>

                    <Grid item xs={12} lg={12} align='center'>
                        <Box mb={2} sx={{ width: '65%' }}>
                            <PrimaryButton
                                fullWidth
                                size='large'
                                variant='contained'
                                onClick={exportReport}
                                buttonText={"GENERATE REPORT (.xlsx)"}
                            >
                            </PrimaryButton>
                        </Box>
                    </Grid>
                </Grid>
            </CommonModal>
        </>
    );
};

export default Greenchill;